import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import { rebootRouter } from '@/api/vendo';
import type { VendoDevice } from '@/pages/Admin/Vendo';

/**
 * Reboot Router (2e-B) — owner-facing confirm dialog for a remote reboot of a
 * main vendo's router. Two roads, and the wording has to follow which one:
 *
 *   MikroTik — the server fires /system/reboot over the WG tunnel and returns
 *   immediately (the box drops the connection as it powers down). It really is
 *   restarting now.
 *
 *   Lite (Ruijie / OrangePi) — the command is QUEUED on the box's heartbeat,
 *   which keeps working while the tunnel is dead. That is the whole reason it
 *   exists: a Ruijie has no other remote reboot, and on 2026-08-11 a box locked
 *   itself out of updates with the only remedy a three-hour drive. It may act in
 *   seconds, or whenever it next comes back — so this dialog reports the
 *   server's own words rather than promising "1–2 minutes".
 *
 * Fire-and-confirm either way: no polling. The device card shows "Updating"
 * during the window because the server stamps updatingAt.
 */

type Phase = 'confirm' | 'sending' | 'done' | 'error';

const RebootRouter: React.FC<{ data: { device: VendoDevice } }> = ({ data }) => {
   const device = data.device;
   const { handleClose } = useUIStore();
   const [phase, setPhase] = useState<Phase>('confirm');
   const [errMsg, setErrMsg] = useState('');
   // The server's own words. A MikroTik really is restarting right now; a Lite
   // box has been HANDED the command and may act on it in seconds or whenever it
   // next comes back. Promising "1–2 minutes" for the second case would be a
   // lie, and this dialog exists precisely because operators were guessing.
   const [doneMsg, setDoneMsg] = useState('');

   const doReboot = async () => {
      setPhase('sending');
      try {
         const r = await rebootRouter(device._id);
         setDoneMsg(
            r?.queued
               ? r.msg ||
                    'Reboot sent. The box will restart on its next check-in — or as soon as it comes back.'
               : 'Reboot sent. The router is restarting and should be back in about 1–2 minutes.'
         );
         setPhase('done');
      } catch (err) {
         setErrMsg(apiErr(err, 'Could not reboot the router'));
         setPhase('error');
      }
   };

   return (
      <Wrap>
         {phase === 'confirm' && (
            <>
               <Lead>
                  Restart the router for <b>{device.name}</b> now?
               </Lead>
               <Note>
                  ⚠ Every customer loses WiFi for about <b>1–2 minutes</b> while
                  the router reboots and reconnects. Paid time is <b>kept</b> —
                  customers keep their remaining minutes when it comes back.
               </Note>
               <Actions>
                  <Ghost onClick={handleClose}>Cancel</Ghost>
                  <Primary onClick={doReboot}>Reboot now</Primary>
               </Actions>
            </>
         )}

         {phase === 'sending' && (
            <Center>
               <Spinner />
               <Lead style={{ textAlign: 'center' }}>Sending reboot…</Lead>
            </Center>
         )}

         {phase === 'done' && (
            <Center>
               <BigCheck>✓</BigCheck>
               <Lead style={{ textAlign: 'center' }}>{doneMsg}</Lead>
               <Actions>
                  <Primary onClick={handleClose}>Done</Primary>
               </Actions>
            </Center>
         )}

         {phase === 'error' && (
            <>
               <ErrBox>{errMsg}</ErrBox>
               <Actions>
                  <Ghost onClick={handleClose}>Close</Ghost>
                  <Primary onClick={doReboot}>Try again</Primary>
               </Actions>
            </>
         )}
      </Wrap>
   );
};

export default RebootRouter;

function apiErr(err: unknown, fallback: string): string {
   return (
      (err as { response?: { data?: { error?: string } } })?.response?.data
         ?.error || fallback
   );
}

const Wrap = styled.div`
   padding: 4px 2px;
   max-width: 460px;
`;
const Lead = styled.p`
   margin: 0 0 14px;
   font-size: 14px;
   line-height: 1.55;
   color: ${colors.DARK};
`;
const Note = styled.div`
   margin: 0 0 18px;
   padding: 10px 12px;
   border-radius: 8px;
   background: ${colors.WARNING}15;
   border: 1px solid ${colors.WARNING}40;
   font-size: 12.5px;
   line-height: 1.5;
   color: ${colors.DARK};
`;
const Actions = styled.div`
   display: flex;
   gap: 10px;
   justify-content: flex-end;
   margin-top: 8px;
`;
const Primary = styled.button`
   border: none;
   border-radius: 8px;
   padding: 10px 18px;
   font-size: 13px;
   font-weight: 600;
   color: #fff;
   background: ${colors.PRIMARY};
   cursor: pointer;
   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
   }
`;
const Ghost = styled.button`
   border: 1px solid #e0e0e0;
   border-radius: 8px;
   padding: 10px 16px;
   font-size: 13px;
   background: #fff;
   color: ${colors.GRAY};
   cursor: pointer;
`;
const Center = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 12px;
   padding: 12px 0;
`;
const Spinner = styled.div`
   width: 34px;
   height: 34px;
   border-radius: 50%;
   border: 3px solid ${colors.PRIMARY}30;
   border-top-color: ${colors.PRIMARY};
   animation: spin 0.8s linear infinite;
   @keyframes spin {
      to {
         transform: rotate(360deg);
      }
   }
`;
const BigCheck = styled.div`
   width: 44px;
   height: 44px;
   border-radius: 50%;
   background: ${colors.SUCCESS};
   color: #fff;
   font-size: 24px;
   font-weight: 700;
   display: flex;
   align-items: center;
   justify-content: center;
`;
const ErrBox = styled.div`
   margin-bottom: 16px;
   padding: 12px 14px;
   border-radius: 8px;
   background: ${colors.DANGER}10;
   border: 1px solid ${colors.DANGER}40;
   font-size: 13px;
   line-height: 1.5;
   color: ${colors.DANGER};
`;
